import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckSquare, Square, Plus, Pencil, Trash2, ListChecks, Wallet, Receipt } from 'lucide-react';
import { checklistAPI } from '../services/api';
import { useCategoriesQuery } from '../hooks/useQueries';
import { useConfig } from '../context/ConfigContext';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

const currentMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = () => new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

export default function ChecklistPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { formatAmount } = useConfig();
  const { toast } = useToast();
  const { data: categories = [] } = useCategoriesQuery();
  const [showForm, setShowForm] = useState(false);
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { user, updateProfile, refreshUser } = useAuth();
  const monthlyIncome = user?.notificationPreferences?.checklistMonthlyIncome || 0;

  useEffect(() => { refreshUser(); }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['checklist'],
    queryFn: async () => {
      const res = await checklistAPI.getAll();
      return res.data?.data?.checklistItems || res.data?.checklistItems || [];
    },
  });

  const items = data || [];
  const month = currentMonth();

  const toggleMutation = useMutation({
    mutationFn: (id) => checklistAPI.toggle(id, month),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['checklist'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => checklistAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist'] });
      toast.success('Item deleted');
      setDeleteConfirm(null);
    },
  });

  const { checkedTotal, grandTotal, checkedCount } = useMemo(() => {
    let ct = 0, gt = 0, cc = 0;
    for (const item of items) {
      gt += item.amount || 0;
      if (item.checkedMonth === month) { ct += item.amount || 0; cc++; }
    }
    return { checkedTotal: ct, grandTotal: gt, checkedCount: cc };
  }, [items, month]);

  const pct = grandTotal > 0 ? (checkedTotal / grandTotal) * 100 : 0;
  const savings = monthlyIncome > 0 ? monthlyIncome - grandTotal : 0;

  // Open the transaction form pre-filled from a checklist item: amount,
  // name → description, category (resolved from the item's group) and expense.
  const handleAddTransaction = (item) => {
    const cat = item.group
      ? categories.find(
          (c) => c.type === 'expense' && c.name?.toLowerCase() === item.group.toLowerCase()
        )
      : null;
    navigate('/transactions/new', {
      state: {
        prefill: {
          type: 'expense',
          amount: item.amount || undefined,
          description: item.name,
          category: cat?._id,
        },
      },
    });
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const item of items) {
      const group = item.group || 'General';
      if (!g[group]) g[group] = [];
      g[group].push(item);
    }
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ListChecks className="w-6 h-6" />
            Monthly Checklist
          </h1>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium text-sm"
        >
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div>

      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">{monthLabel()}</p>
        <button
          onClick={() => setShowIncomeForm(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg text-xs font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
        >
          <Wallet className="w-3.5 h-3.5" />
          {monthlyIncome > 0 ? formatAmount(monthlyIncome) : 'Set income'}
          <Pencil className="w-3 h-3" />
        </button>
      </div>

      {/* Progress */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-5 mb-6 text-white">
        <div className="flex justify-between items-end mb-3">
          <div>
            <p className="text-sm text-slate-400">Paid this month</p>
            <p className="text-2xl font-bold">{formatAmount(checkedTotal)} <span className="text-sm font-normal text-slate-400">/ {formatAmount(grandTotal)}</span></p>
          </div>
          <p className="text-sm text-slate-400">{checkedCount} / {items.length} done</p>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2.5">
          <div className="bg-white h-2.5 rounded-full transition-all duration-300" style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
        {monthlyIncome > 0 ? (
          <div className="flex gap-6 mt-3">
            <div>
              <p className="text-[10px] text-slate-500">Income</p>
              <p className="text-sm font-semibold">{formatAmount(monthlyIncome)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500">Bills</p>
              <p className="text-sm font-semibold">{formatAmount(grandTotal)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500">Savings</p>
              <p className={`text-sm font-semibold ${savings > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatAmount(savings)}</p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-400 mt-2">{pct.toFixed(0)}% completed</p>
        )}
      </div>

      {/* Items */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <ListChecks className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="font-semibold text-gray-700 dark:text-gray-300">No items yet</p>
          <p className="text-sm text-gray-400 mt-1">Add your monthly bills, EMIs, and recurring expenses</p>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
            Add first item
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([group, groupItems]) => (
            <div key={group}>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{group}</p>
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                {groupItems.map((item) => {
                  const checked = item.checkedMonth === month;
                  return (
                    <div key={item._id} className="flex items-center px-4 py-3">
                      <button
                        onClick={() => toggleMutation.mutate(item._id)}
                        className="flex-shrink-0 mr-3"
                      >
                        {checked
                          ? <CheckSquare className="w-5 h-5 text-emerald-500" />
                          : <Square className="w-5 h-5 text-gray-300" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm font-medium ${checked ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>
                          {item.name}
                        </span>
                        {item.note && (
                          <p className={`text-xs mt-0.5 truncate ${checked ? 'text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
                            {item.note}
                          </p>
                        )}
                      </div>
                      <span className={`text-sm font-semibold mr-3 ${checked ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                        {formatAmount(item.amount)}
                      </span>
                      <button onClick={() => handleAddTransaction(item)} className="p-1.5 text-gray-400 hover:text-emerald-600" title="Add transaction">
                        <Receipt className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => { setEditing(item); setShowForm(true); }} className="p-1.5 text-gray-400 hover:text-blue-500" title="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setDeleteConfirm(item)} className="p-1.5 text-gray-400 hover:text-red-500" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <ChecklistFormModal
          item={editing}
          toast={toast}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {showIncomeForm && (
        <IncomeModal
          current={monthlyIncome}
          onSave={async (v) => {
            try {
              await updateProfile({ checklistMonthlyIncome: v });
            } catch (_) {}
            setShowIncomeForm(false);
          }}
          onClose={() => setShowIncomeForm(false)}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white dark:bg-gray-800 max-w-sm w-full mx-4 rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete item?</h3>
            <p className="text-sm text-gray-500 mb-5">"{deleteConfirm.name}" will be removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300">Cancel</button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm._id)}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IncomeModal({ current, onSave, onClose }) {
  const [value, setValue] = useState(current > 0 ? current.toString() : '');

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-t-2xl sm:rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Monthly Income</h2>
        <p className="text-sm text-gray-500 mb-4">Set your expected monthly income to track savings</p>
        <input
          type="number" value={value} onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. 50000" min="0" autoFocus
          className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm mb-4"
        />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300">Cancel</button>
          <button onClick={() => onSave(parseFloat(value) || 0)} className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">Save</button>
        </div>
      </div>
    </div>
  );
}

function ChecklistFormModal({ item, toast, onClose }) {
  const queryClient = useQueryClient();
  const { data: categories = [] } = useCategoriesQuery();
  const expenseCategories = categories.filter((c) => c.type === 'expense');
  const [name, setName] = useState(item?.name || '');
  const [note, setNote] = useState(item?.note || '');
  const [amount, setAmount] = useState(item?.amount?.toString() || '');
  const [group, setGroup] = useState(item?.group || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body = { name: name.trim(), note: note.trim() || undefined, amount: parseFloat(amount) || 0, group: group.trim() || undefined };
      if (item) {
        await checklistAPI.update(item._id, body);
      } else {
        await checklistAPI.create(body);
      }
      queryClient.invalidateQueries({ queryKey: ['checklist'] });
      toast.success(item ? 'Updated' : 'Added');
      onClose();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-t-2xl sm:rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{item ? 'Edit Item' : 'Add Item'}</h2>
        <form onSubmit={handleSave} className="space-y-3">
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Item name (e.g. Electricity)"
            className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            autoFocus
          />
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            rows={2}
            maxLength={500}
            className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none"
          />
          <input
            type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="Amount" min="0" step="0.01"
            className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
          <select
            value={group} onChange={(e) => setGroup(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="">Category (optional)</option>
            {expenseCategories.map((c) => (
              <option key={c._id} value={c.name}>{c.name}</option>
            ))}
            {group && !expenseCategories.some((c) => c.name === group) && (
              <option value={group}>{group}</option>
            )}
          </select>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
              {saving ? 'Saving...' : item ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
